import rawCatalog from '../assets/sheet_catalog.json';

/**
 * Planches LimeZu de l'onglet « Planches » de l'éditeur — générées par
 * `tools/assets-cli/src/ingest-sheets.ts`. Un tampon = n'importe quel
 * rectangle de cases d'une planche, comme dans Tiled.
 *
 * ID d'un tampon (stocké tel quel dans `map_furniture.furniture_id`) :
 * `lz_{planche}_r{x}_{y}_{l}x{h}[_fx]`, en cases, `_fx` = retourné
 * horizontalement — le même format que les objets de planche du catalogue
 * mobilier, qui sont donc eux-mêmes des tampons.
 */
export interface SheetCatalogEntry {
  slug: string;
  label: string;
  /** Dimensions en cases. */
  cols: number;
  rows: number;
  /** Rangées par page (la dernière peut en avoir moins). */
  pageRows: number;
  /** URL versionnée de chaque page, de haut en bas. */
  pages: string[];
  /**
   * `false` : absente de la liste de l'onglet « Planches » (ensembles LimeZu,
   * posés depuis l'onglet « Objets ») — mais ses tampons restent valides.
   */
  listed?: boolean;
}

export interface SheetStamp {
  sheet: string;
  /** Rectangle en cases dans la planche. */
  tx: number;
  ty: number;
  tw: number;
  th: number;
  flipX: boolean;
}

interface SheetCatalogFile {
  tileSize: number;
  sheets: SheetCatalogEntry[];
}

const catalogFile = rawCatalog as SheetCatalogFile;

export const SHEET_TILE_SIZE = catalogFile.tileSize;
export const sheetCatalog = catalogFile.sheets;

const bySlug = new Map(sheetCatalog.map((s) => [s.slug, s]));

export function findSheet(slug: string): SheetCatalogEntry | undefined {
  return bySlug.get(slug);
}

export function stampId(s: SheetStamp): string {
  return `lz_${s.sheet}_r${s.tx}_${s.ty}_${s.tw}x${s.th}${s.flipX ? '_fx' : ''}`;
}

/** Décode un ID de tampon ; `null` si ce n'est pas un rectangle valide d'une planche publiée. */
export function parseStampId(id: string): SheetStamp | null {
  const m = id.match(/^lz_(.+)_r(\d+)_(\d+)_(\d+)x(\d+)(_fx)?$/);
  if (!m) return null;
  const sheet = findSheet(m[1]!);
  if (!sheet) return null;
  const [tx, ty, tw, th] = m.slice(2, 6).map(Number) as [number, number, number, number];
  if (tw < 1 || th < 1 || tx + tw > sheet.cols || ty + th > sheet.rows) return null;
  return { sheet: sheet.slug, tx, ty, tw, th, flipX: !!m[6] };
}

/** Pages de la planche couvertes par le tampon (indices, de haut en bas). */
export function stampPages(s: SheetStamp): number[] {
  const sheet = findSheet(s.sheet);
  if (!sheet) return [];
  const first = Math.floor(s.ty / sheet.pageRows);
  const last = Math.floor((s.ty + s.th - 1) / sheet.pageRows);
  return Array.from({ length: last - first + 1 }, (_, i) => first + i);
}
